import Head from 'next/head';

import type { Seo } from '@tac/sanity-client/types';
import type { StructuredDataSchema } from '@tac/structured-data';
import { StructuredData } from './structured-data';
import { urlFor } from '@/utils/sanity-image';

export type SEOProps = Seo & {
  structuredData?: StructuredDataSchema[];
};

export const SEO = (props: SEOProps) => {
  const { title, description, robots, opengraphImage, fbProfileId, structuredData } = props;

  const opengraphImageUrl = urlFor(opengraphImage)?.url();

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="theme-color" content="#ffffff" />
        <meta name="viewport" content="initial-scale=1.0, width=device-width" key="viewport" />
        <link rel="icon" type="image/png" sizes="32x32" href="/images/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/images/favicon-16x16.png" />
        <link rel="shortcut icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/images/apple-touch-icon.png" />

        {/* Robots meta tags */}
        {!robots && process.env.VERCEL_ENV == 'production' && <meta name="robots" content="all" />}
        {robots?.noindex && <meta name="robots" content="noindex" />}
        {robots?.nofollow && <meta name="robots" content="nofollow" />}
        {robots?.noarchive && <meta name="robots" content="noarchive" />}
        {robots?.nosnippet && <meta name="robots" content="nosnippet" />}
        {robots?.notranslate && <meta name="robots" content="notranslate" />}
        {robots?.noimageindex && <meta name="robots" content="noimageindex" />}

        {/* OpenGraph meta tags */}
        {fbProfileId && <meta property="fb:profile_id" content={fbProfileId} />}
        {opengraphImageUrl && <meta property="og:image" content={opengraphImageUrl} />}
        <meta property="og:url" content={process.env.VERCEL_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:site_name" content={title} />
        <meta property="og:locale" content="en-AU" />

        {/* Twitter meta tags */}
        {opengraphImageUrl && <meta name="twitter:image" content={opengraphImageUrl} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" />
        <meta name="twitter:url" content={process.env.VERCEL_URL} />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
      </Head>
      <StructuredData schemas={structuredData} />
    </>
  );
};
